/**
 * Валидация полей — единый источник правды для приложений и бэкенда.
 * Возвращает ключ i18n при ошибке (validation.*) или null при успехе.
 * Соответствие стандартам: телефон E.164 (международный), email упрощённый HTML5-подобный,
 * VK/Telegram по правилам платформ, цена — неотрицательное число в пределах лимита.
 */
import { VALIDATION_LIMITS, VALIDATION_PATTERNS, BUSINESS_LIMITS } from './constants/index.js';
import { isPossibleAndValidPhone, isValidPhone } from './phone.js';
/** Доверенные домены MAX (allowlist). Поддомены разрешены: host === allowed или host.endsWith('.' + allowed). */
export const MAX_HOST_ALLOWLIST = ['max.ru', 'max.mail.ru', 'web.max.ru'];
/** VK: id + 1–20 цифр или username 3–32 (латиница, цифры, точка, подчёркивание). */
const VK_ID_REGEX = /^id\d{1,20}$/i;
const VK_USERNAME_REGEX = /^[a-zA-Z0-9._]{3,32}$/;
/** Telegram: 5–32, латиница/цифры/_, не начинается и не заканчивается _, без __ подряд. */
const TELEGRAM_USERNAME_REGEX = /^(?!_)(?!.*__)[A-Za-z0-9_]{5,32}(?<!_)$/;
const MAX_PROTOCOL_REGEX = /^https:\/\//i;
const MAX_URL_LEN_MIN = 10;
const MAX_URL_LEN_MAX = 255;
const WHATSAPP_E164_REGEX = /^\+7\d{10}$/;
function isHostAllowed(hostname, allowlist = MAX_HOST_ALLOWLIST) {
    const normalizedHost = hostname.toLowerCase();
    return allowlist.some((allowed) => {
        const a = allowed.toLowerCase();
        return normalizedHost === a || normalizedHost.endsWith(`.${a}`);
    });
}
function isForbiddenHost(hostname) {
    const h = hostname.toLowerCase();
    return h.includes('wa.me') || h.includes('whatsapp') || h.includes('vk.com') || h.includes('t.me');
}
/**
 * Нормализует ввод VK в канон https://vk.com/<id|username>.
 * Принимает id123/username/@username/vk.com/...; возвращает url и canonical (часть после домена) или null если невалидно.
 */
export function normalizeVkToUrl(input) {
    const raw = input.trim();
    if (!raw)
        return null;
    let candidate = raw;
    if (raw.includes('vk.com')) {
        try {
            const url = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
            const seg = url.pathname.split('/').filter(Boolean)[0];
            if (!seg)
                return null;
            candidate = seg;
        }
        catch {
            return null;
        }
    }
    candidate = candidate.replace(/^@+/, '');
    if (/^\d{1,20}$/.test(candidate))
        candidate = 'id' + candidate;
    if (!validateVkCanonical(candidate))
        return null;
    return { url: `https://vk.com/${candidate}`, canonical: candidate };
}
/** Проверяет канон VK: id + 1–20 цифр или username 3–32 (латиница/цифры/._). */
export function validateVkCanonical(canonical) {
    const s = canonical.trim();
    return VK_ID_REGEX.test(s) || VK_USERNAME_REGEX.test(s);
}
/**
 * Нормализует Telegram: принимает @, t.me, просто имя; возвращает username без @ или null.
 */
export function normalizeTelegramUsernameValue(input) {
    const trimmed = input.trim();
    if (!trimmed)
        return null;
    let username = trimmed;
    if (trimmed.includes('t.me')) {
        try {
            const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
            username = url.pathname.replace(/^\/+|\/+$/g, '');
        }
        catch {
            return null;
        }
    }
    username = username.replace(/^@+/, '');
    if (!validateTelegramUsernameValue(username))
        return null;
    return { username };
}
/** Валидирует Telegram username (5–32, латиница/цифры/_). */
export function validateTelegramUsernameValue(username) {
    return TELEGRAM_USERNAME_REGEX.test(username.trim());
}
/**
 * Нормализация ссылки MAX: приводит к https://, проверяет allowlist доменов.
 * Возвращает url и host или null.
 */
export function normalizeMaxInviteUrl(input, opts) {
    const trimmed = input.trim();
    if (!trimmed || /\s/.test(trimmed))
        return null;
    const allowlist = opts?.allowlist ?? MAX_HOST_ALLOWLIST;
    const withProtocol = MAX_PROTOCOL_REGEX.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
    try {
        const url = new URL(withProtocol);
        if (url.protocol !== 'https:')
            return null;
        const host = url.hostname;
        if (isForbiddenHost(host) || !isHostAllowed(host, allowlist))
            return null;
        const resultUrl = url.toString();
        if (resultUrl.length < MAX_URL_LEN_MIN || resultUrl.length > MAX_URL_LEN_MAX)
            return null;
        return { url: resultUrl, host };
    }
    catch {
        return null;
    }
}
/** Валидирует MAX invite URL по allowlist доменов. */
export function validateMaxInviteUrl(url, opts) {
    return !!normalizeMaxInviteUrl(url, opts);
}
/**
 * Нормализует российский WhatsApp номер к E.164 +7XXXXXXXXXX.
 * Принимает +7/8/79... или wa.me/79...; возвращает e164 и digits (10 последних) либо null.
 */
export function normalizeWhatsappE164Ru(input) {
    const trimmed = input.trim();
    if (!trimmed)
        return null;
    let digits = trimmed.replace(/\D/g, '');
    if (digits.startsWith('7') && digits.length === 11) {
        digits = digits.slice(1);
    }
    else if (digits.startsWith('8') && digits.length === 11) {
        digits = digits.slice(1);
    }
    else if (digits.length === 10) {
        // already local
    }
    else if (digits.length > 10) {
        digits = digits.slice(-10);
    }
    if (digits.length !== 10)
        return null;
    const e164 = `+7${digits}`;
    return validateWhatsappE164Ru(e164) ? { e164, digits } : null;
}
/** Проверяет что номер в формате +7XXXXXXXXXX. */
export function validateWhatsappE164Ru(e164) {
    return WHATSAPP_E164_REGEX.test(e164.trim());
}
/** Проверяет, является ли строка ссылкой MAX (используется для legacy fallback). */
export function isMaxInviteUrl(input, opts) {
    return !!normalizeMaxInviteUrl(input, opts);
}
/** Нормализация Telegram username: убирает лишние @, возвращает с одним @ */
export function normalizeTelegramUsername(username) {
    const trimmed = username.trim();
    if (!trimmed)
        return '';
    const cleaned = trimmed.replace(/^@+/, '');
    return cleaned ? `@${cleaned}` : '';
}
/** Нормализация поля «Мессенджер Max»: только max.ru-ссылки получают https; номер и прочее — без изменений. Никогда не создаём wa.me из номера. */
export function normalizeWhatsApp(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return '';
    if (isMaxInviteUrl(trimmed)) {
        const normalized = normalizeMaxInviteUrl(trimmed);
        return normalized ? normalized.url : trimmed;
    }
    return trimmed;
}
/** Ссылка для кнопки «Мессенджер Max»: URL — как есть; max. без протокола — с https; только номер — tel: */
export function getMessengerMaxHref(value) {
    const link = (value ?? '').trim();
    if (!link)
        return '#';
    if (/^https?:\/\//i.test(link))
        return link;
    if (/^[\d\s\-+()]+$/.test(link.replace(/\s/g, '')))
        return `tel:${link.replace(/\D/g, '')}`;
    if (isMaxInviteUrl(link)) {
        const normalized = normalizeMaxInviteUrl(link);
        return normalized ? normalized.url : `https://${link.replace(/^\/+/, '')}`;
    }
    if (link.includes('max.'))
        return `https://${link.replace(/^\/+/, '')}`;
    return link;
}
/**
 * Нормализация части имени (имя/фамилия): trim, схлопывание множественных пробелов и дефисов.
 * Для использования перед валидацией и отображением.
 */
export function normalizeNamePart(value) {
    return value
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/-+/g, '-');
}
/**
 * Строгая валидация части имени (имя или фамилия).
 * Допустимы: буквы (кириллица/латиница), пробел, дефис, апостроф (' и ').
 * Минимум 2 буквы (или 1 буква для редких имён вроде «Ян», «Ю»).
 * После normalizeNamePart не должно быть двойных разделителей.
 */
const NAME_PART_REGEX = /^[\p{L}]([\p{L}\s\-'\u2019]*[\p{L}])?$/u;
export function isNamePartValid(value) {
    const n = normalizeNamePart(value);
    if (!n)
        return false;
    if (n.length > VALIDATION_LIMITS.CLIENT_NAME_MAX)
        return false;
    return NAME_PART_REGEX.test(n);
}
/** Коды ошибок валидации имени для API (бэкенд возвращает, фронт переводит в текст). */
export const NAME_VALIDATION_ERROR_CODES = {
    NAME_REQUIRED: 'NAME_REQUIRED',
    INVALID_FIRST_NAME: 'INVALID_FIRST_NAME',
    INVALID_LAST_NAME: 'INVALID_LAST_NAME',
};
/**
 * Валидация полного имени (clientName): нормализация, разбиение на first + last, проверка частей.
 * Возвращает код ошибки или null.
 */
export function validateClientNameFull(clientName) {
    const normalized = normalizeNamePart(clientName);
    if (!normalized)
        return NAME_VALIDATION_ERROR_CODES.NAME_REQUIRED;
    const parts = normalized.split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return NAME_VALIDATION_ERROR_CODES.NAME_REQUIRED;
    const first = parts[0];
    const last = parts.slice(1).join(' ');
    if (!isNamePartValid(first))
        return NAME_VALIDATION_ERROR_CODES.INVALID_FIRST_NAME;
    if (last && !isNamePartValid(last))
        return NAME_VALIDATION_ERROR_CODES.INVALID_LAST_NAME;
    return null;
}
/** Имя: 2–CLIENT_NAME_MAX символов, не только пробелы. Пустое — null (звездочка уже обозначает обязательность). */
export function validateName(name) {
    const trimmed = name.trim();
    if (!trimmed)
        return null;
    if (trimmed.length < 2)
        return 'validation.nameMin';
    if (trimmed.length > VALIDATION_LIMITS.CLIENT_NAME_MAX)
        return 'validation.nameMax';
    if (/^\s+$/.test(trimmed))
        return 'validation.nameSpaces';
    return null;
}
/** Email: опционально; при наличии — паттерн VALIDATION_PATTERNS.EMAIL */
export function validateEmail(email) {
    if (!email || !email.trim())
        return null;
    if (!VALIDATION_PATTERNS.EMAIL.test(email.trim()))
        return 'validation.emailInvalid';
    if (email.length > VALIDATION_LIMITS.EMAIL_MAX)
        return 'validation.emailMax';
    return null;
}
/** Телефон: опционально; при наличии — валидный E.164 (shared/phone + libphonenumber) */
export function validatePhone(phone) {
    if (!phone || !phone.trim())
        return null;
    if (!isPossibleAndValidPhone(phone))
        return 'validation.phoneInvalid';
    return null;
}
/** Время HH:MM (0–23, 0–59) */
export function validateTime(time) {
    if (!VALIDATION_PATTERNS.TIME.test(time))
        return 'validation.timeInvalid';
    const [hours, minutes] = time.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes))
        return 'validation.timeInvalid';
    if (hours < 0 || hours > 23)
        return 'validation.timeHours';
    if (minutes < 0 || minutes > 59)
        return 'validation.timeMinutes';
    return null;
}
/** Диапазон времени и минимальная длительность рабочего дня */
export function validateTimeRange(start, end) {
    const startErr = validateTime(start);
    if (startErr)
        return startErr;
    const endErr = validateTime(end);
    if (endErr)
        return endErr;
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    if (startMinutes >= endMinutes)
        return 'validation.timeRange';
    if (endMinutes - startMinutes < BUSINESS_LIMITS.MIN_INTERVAL_DURATION_MINUTES)
        return 'validation.timeMinDuration';
    return null;
}
/** Цена: число в [PRICE_MIN, PRICE_MAX] (рубли) */
export function validatePrice(value) {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num))
        return 'validation.priceInvalid';
    if (num < VALIDATION_LIMITS.PRICE_MIN)
        return 'validation.priceMin';
    if (num > VALIDATION_LIMITS.PRICE_MAX)
        return 'validation.priceMax';
    return null;
}
/** Из ссылки vk.com извлекает идентификатор для API (id или screen name) */
export function parseVkUrlToIdentifier(url) {
    const trimmed = url.trim();
    if (!trimmed || !trimmed.includes('vk.com'))
        return null;
    try {
        const u = new URL(trimmed);
        if (!/^(www\.)?vk\.com$/i.test(u.hostname))
            return null;
        const segment = u.pathname.replace(/^\/+|\/+$/g, '').split('/')[0];
        if (!segment)
            return null;
        const idMatch = /^id(\d+)$/i.exec(segment);
        return idMatch ? idMatch[1] : segment;
    }
    catch {
        return null;
    }
}
/** VK URL: vk.com, формат ссылки */
export function validateVkUrl(url) {
    const trimmed = url.trim();
    if (!trimmed)
        return null;
    if (!trimmed.includes('vk.com'))
        return 'validation.vkUrl';
    try {
        if (!VALIDATION_PATTERNS.VK_URL.test(trimmed))
            return 'validation.vkUrlFormat';
        new URL(trimmed);
        return null;
    }
    catch {
        return 'validation.vkUrlFormat';
    }
}
/** Telegram username: 5–32, латиница/цифры/_, без _ по краям и без __ (без @). */
export function validateTelegramUsername(username) {
    const cleaned = username.trim().replace(/^@+/, '');
    if (!cleaned)
        return null;
    if (!validateTelegramUsernameValue(cleaned))
        return 'validation.telegramFormat';
    return null;
}
/** Мессенджер Max: пусто — ок; иначе валидный телефон (E.164) или max-ссылка / legacy wa.me */
export function validateMessengerLink(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return null;
    const digits = trimmed.replace(/\D/g, '');
    const looksLikePhone = /^[\d\s\-+()]+$/.test(trimmed);
    if (looksLikePhone && digits.length >= VALIDATION_LIMITS.PHONE_MIN && isValidPhone(trimmed)) {
        return null;
    }
    if (isMaxInviteUrl(trimmed))
        return null;
    if (trimmed.includes('wa.me/') || trimmed.includes('max.mail.ru'))
        return null;
    return 'validation.messengerLink';
}
/** Адрес: длина в пределах ADDRESS_MAX */
export function validateAddress(address, required) {
    const trimmed = address.trim();
    if (!trimmed)
        return required ? 'validation.required' : null;
    if (trimmed.length < 2)
        return 'validation.addressMin';
    if (trimmed.length > VALIDATION_LIMITS.ADDRESS_MAX)
        return 'validation.addressMax';
    return null;
}
/** Длина текста */
export function validateTextLength(text, maxLength, _fieldName) {
    if (text.length > maxLength)
        return 'validation.textLength';
    return null;
}
/** Публичное название онлайн-записи: 2–60 символов */
export const ONLINE_BOOKING_TITLE_MIN = 2;
export const ONLINE_BOOKING_TITLE_MAX = 60;
export function validateOnlineBookingRequiredFields(ob) {
    if (!ob.enabled)
        return true;
    const title = (ob.title || '').trim();
    const phoneOk = ob.phone ? isValidPhone(ob.phone) : false;
    const address = (ob.address || '').trim();
    if (title.length < ONLINE_BOOKING_TITLE_MIN || title.length > ONLINE_BOOKING_TITLE_MAX)
        return false;
    if (!phoneOk)
        return false;
    if (!address)
        return false;
    return true;
}
//# sourceMappingURL=validation.js.map